<ng-container *transloco="let t; read: 'providers'">
  <div class="flex flex-col gap-4 rounded-card bg-card p-6 shadow-hairline">
    <div class="flex items-center justify-between gap-3">
      <span class="font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ t('feedPage.health.title') }}</span>
      @if (!isCreate() && !isLoading()) {
        <app-label-badge [dot]="overall().dot">{{ t('gtfsDiagnostics.healthCard.status.' + overall().key) }}</app-label-badge>
      }
    </div>

    @if (isCreate()) {
      <div class="flex flex-col gap-1.5">
        <span class="text-[14px] font-semibold text-ink-1">{{ t('gtfsDiagnostics.healthCard.createTitle') }}</span>
        <p class="m-0 text-[13px] leading-5 text-ink-2">{{ t('gtfsDiagnostics.healthCard.createBody') }}</p>
      </div>
    } @else if (isLoading()) {
      <span class="text-[13px] text-ink-3">—</span>
    } @else if (!snapshot()) {
      <div class="flex flex-col gap-1.5">
        <span class="text-[14px] font-semibold text-ink-1">{{ t('gtfsDiagnostics.healthCard.noSnapshotTitle') }}</span>
        <p class="m-0 text-[13px] leading-5 text-ink-2">{{ t('gtfsDiagnostics.healthCard.noSnapshotBody') }}</p>
      </div>
    } @else {
      @let s = snapshot()!;
      @if (d(); as p) {
        @if (p.status !== 'OK') {
          <p class="m-0 text-[13px] leading-5 text-ink-1">{{ p.message }}</p>
        }
      }
      @if (isStale()) {
        <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[12px] leading-[18px] text-danger">
          <span class="font-bold">{{ t('feedPage.health.staleTitle', { minutes: staleMinutes() }) }}</span> {{ t('feedPage.health.staleSuffix') }}
        </p>
      }

      @if (rawOpen() || !d()) {
        @if (!d()) {
          <p class="m-0 text-[13px] text-ink-3">{{ t('gtfsDiagnostics.unexpectedShape') }}</p>
        }
        <pre class="m-0 max-h-[420px] overflow-auto rounded-control bg-sunken p-3 font-mono text-[11px] leading-4 text-ink-2">{{ s.payload | json }}</pre>
      } @else {
        @let p = d()!;
        <!-- Static schedule -->
        <div class="flex flex-col gap-2.5">
          <div class="flex items-center justify-between gap-2">
            <span class="text-[13px] font-bold text-ink-1">{{ t('gtfsDiagnostics.healthCard.staticTitle') }}</span>
            <app-label-badge [dot]="staticDot(p.static.state)">{{ t('gtfsDiagnostics.healthCard.staticState.' + p.static.state) }}</app-label-badge>
          </div>
          @if (p.static.state === 'ACTIVE') {
            <div class="grid grid-cols-[96px_1fr] items-baseline gap-x-3 gap-y-2 text-[13px]">
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.static.version') }}</span>
              <span class="font-num text-ink-1">{{ p.static.versionNumber != null ? 'v' + p.static.versionNumber : '—' }}</span>
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.static.service') }}</span>
              <span class="text-ink-1">
                @if (p.static.validFrom && p.static.validTo) {
                  {{ p.static.validFrom | date: 'mediumDate' }} – {{ p.static.validTo | date: 'mediumDate' }}
                } @else {
                  {{ t('gtfsDiagnostics.static.noServiceDates') }}
                }
              </span>
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.static.imported') }}</span>
              <span class="text-ink-1" [attr.title]="p.static.importedAt">{{ relativeTime(p.static.importedAt) }}</span>
            </div>
          } @else {
            <p class="m-0 text-[13px] leading-[19px] text-ink-2">{{ t('gtfsDiagnostics.static.stateHelp.' + p.static.state) }}</p>
          }
          @if (p.static.importing) {
            <span class="flex items-center gap-2 text-[12px] text-ink-2">
              <span class="h-2 w-2 flex-none rounded-full bg-[var(--colorscales-orange-300)]"></span>{{ t('gtfsDiagnostics.healthCard.importingNewer') }}
            </span>
          }
          @if (p.static.lastImportError) {
            <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[12px] leading-[18px] text-danger">
              <span class="font-bold">{{ t(p.static.state === 'ACTIVE' ? 'gtfsDiagnostics.healthCard.newerImportFailed' : 'gtfsDiagnostics.healthCard.importFailed') }}</span>
              {{ p.static.lastImportError }}
            </p>
          }
        </div>

        <!-- Realtime -->
        <div class="flex flex-col gap-2.5 border-t border-hairline pt-4">
          <div class="flex items-center justify-between gap-2">
            <span class="text-[13px] font-bold text-ink-1">{{ t('gtfsDiagnostics.realtime.title') }}</span>
            <app-label-badge [dot]="realtimeDot(p.realtime.state)">{{ t('gtfsDiagnostics.healthCard.realtimeState.' + p.realtime.state) }}</app-label-badge>
          </div>
          @if (p.realtime.state === 'DISABLED' || p.realtime.state === 'UNKNOWN') {
            <p class="m-0 text-[13px] leading-[19px] text-ink-2">{{ t('gtfsDiagnostics.healthCard.realtimeHelp.' + p.realtime.state) }}</p>
          } @else {
            <div class="grid grid-cols-[96px_1fr] items-baseline gap-x-3 gap-y-2 text-[13px]">
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.realtime.lastSuccess') }}</span>
              <span [class.text-danger]="realtimeLagging()" [class.text-ink-1]="!realtimeLagging()" [attr.title]="p.realtime.lastSuccessAt">
                {{ p.realtime.lastSuccessAt ? relativeTime(p.realtime.lastSuccessAt) : t('gtfsDiagnostics.realtime.never') }}
              </span>
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.realtime.feedTimestamp') }}</span>
              <span class="text-ink-1" [attr.title]="p.realtime.feedTimestamp">
                {{ p.realtime.feedTimestamp ? relativeTime(p.realtime.feedTimestamp) : t('gtfsDiagnostics.realtime.notSent') }}
              </span>
              <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('gtfsDiagnostics.realtime.entities') }}</span>
              <span class="font-num text-ink-1">{{ p.realtime.entityCount ?? '—' }}</span>
            </div>
          }
          @if (p.realtime.error) {
            <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[12px] leading-[18px] text-danger">
              <span class="font-bold">{{ t('gtfsDiagnostics.realtime.fetchError') }}</span> {{ p.realtime.error }}
            </p>
          }
          @if (p.realtime.alertsError) {
            <p class="m-0 rounded-control bg-sunken px-3 py-2 text-[12px] leading-[18px] text-ink-1">
              <span class="font-bold">{{ t('gtfsDiagnostics.realtime.alertsError') }}</span> {{ p.realtime.alertsError }}
            </p>
          }
        </div>
      }

      <!-- Footer -->
      <div class="flex flex-col items-start gap-1 border-t border-hairline pt-3 text-[12px] leading-[18px] text-ink-3">
        <span [attr.title]="s.receivedAt">
          {{ t('gtfsDiagnostics.healthCard.footer', { time: relativeTime(s.receivedAt), stops: d()?.monitoredStops ?? 0 }) }}
        </span>
        @if (d()) {
          <button type="button" (click)="rawOpen.set(!rawOpen())" class="font-semibold text-link hover:underline">
            {{ t(rawOpen() ? 'feedPage.health.hideRaw' : 'feedPage.health.showRaw') }}
          </button>
        }
      </div>
    }
  </div>
</ng-container>
